import { EVENTS, publish, subscribe } from 'ia-util-pubsub'
import { queryShadow, queryShadowAll } from 'ia-util-helpers'

/**
 * Recharge -> theme pubsub adapter.
 *
 * The Recharge "Storefront Experiences" widget renders inside an (open) shadow
 * root and uses native Shopify selling plans. This adapter is the single bridge
 * between that widget and the rest of the theme: it watches the widget's
 * purchase-option radio (`input[name="purchaseOption"]`) and plan dropdown
 * (`select[name="rc_plan"]`), then republishes the selection as the SAME
 * `cadence:change` event the custom subscription widget emits. That means
 * ia-main-product.js (button price) and ia-product-buy.js (hidden selling_plan)
 * work unchanged — Recharge just becomes an alternate event source.
 *
 * Prices come from the native `[data-rc-plan-prices]` JSON map (money-formatted
 * server-side, so they match the markup) — never scraped from the widget DOM.
 *
 * Active only when <ia-main-product data-use-recharge="true">. Flip the
 * `use_recharge` theme setting to false to fall back to the custom widget.
 */

const RC_PLAN = 'select[name="rc_plan"]'
const RC_PURCHASE_OPTION = 'input[name="purchaseOption"]'

// --- TEMP DEBUG: probe for a native Recharge callback/event API ---
// Set to false (or delete the marked blocks) once we've inspected the console.
const RC_DEBUG = true

// Runs once: dumps any Recharge globals and surfaces every custom event the
// widget dispatches, so we can see whether Recharge gives us a real callback
// for "plan/period selected" or "subscription vs one-time" instead of the
// DOM-scraping this adapter currently does.
let rcProbeInstalled = false
function probeRechargeApi () {
  if (!RC_DEBUG || rcProbeInstalled) return
  rcProbeInstalled = true

  // 1) What Recharge namespaces exist on window?
  const globals = Object.keys(window).filter((k) => /recharge|^rc[A-Z_]|sellingplan|subscription/i.test(k))
  console.log('[rc-probe] window globals matching recharge/rc/sellingplan/subscription:', globals)
  globals.forEach((k) => console.log(`[rc-probe] window.${k} =`, window[k]))

  // 2) Catch ANY custom event the widget fires (we don't know the names yet).
  // Temporarily wrap dispatchEvent so every recharge-ish CustomEvent is logged.
  const nativeDispatch = EventTarget.prototype.dispatchEvent
  EventTarget.prototype.dispatchEvent = function (event) {
    try {
      if (event && /recharge|^rc[:_-]|selling|subscription|purchase|plan/i.test(event.type)) {
        console.log('[rc-probe] event:', event.type, '| target:', this, '| detail:', event.detail)
      }
    } catch (e) { /* never break the page from a probe */ }
    return nativeDispatch.call(this, event)
  }
  console.log('[rc-probe] dispatchEvent monitor installed — interact with the widget to see events.')
}
// --- /TEMP DEBUG ---

class RechargeAdapter {
  constructor (host) {
    this.host = host
    this.productId = host.getAttribute('product-id')
    this.sectionId = host.getAttribute('section-id')
    this.variantInput = host.querySelector('input[name="id"]')
    this.variantId = String(this.variantInput?.value || '')
    this.planPrices = this.readPlanPrices()
    this.select = null
    this.boundOnChange = this.emit.bind(this)

    probeRechargeApi() // TEMP DEBUG
    subscribe(EVENTS.variantChange, this.onVariantChange.bind(this))
    this.waitForWidget()
  }

  readPlanPrices () {
    const el = this.host.querySelector('[data-rc-plan-prices]') || document.querySelector('[data-rc-plan-prices]')
    if (!el) return {}
    try {
      return JSON.parse(el.textContent)
    } catch (e) {
      return {}
    }
  }

  // The app embed hydrates asynchronously — poll briefly until the dropdown exists.
  waitForWidget (attempt = 0) {
    const select = this.findSelect()
    if (select) {
      this.bind(select)
      this.emit()
      return
    }
    if (attempt > 80) return // ~12s, then give up silently
    setTimeout(() => this.waitForWidget(attempt + 1), 150)
  }

  findSelect () {
    return queryShadow(this.host, RC_PLAN) || queryShadow(document, RC_PLAN)
  }

  scopeRoot () {
    // Prefer searching within this product; fall back to document if Recharge
    // mounted the widget outside the section.
    return queryShadow(this.host, RC_PLAN) ? this.host : document
  }

  bind (select) {
    this.select = select
    // `change` is not composed, so it never reaches document — listen directly.
    select.addEventListener('change', this.boundOnChange)
    this.bindPurchaseOptions()
  }

  bindPurchaseOptions () {
    queryShadowAll(this.scopeRoot(), RC_PURCHASE_OPTION).forEach((radio) => {
      radio.removeEventListener('change', this.boundOnChange)
      radio.addEventListener('change', this.boundOnChange)
    })
  }

  unbind () {
    if (this.select) this.select.removeEventListener('change', this.boundOnChange)
  }

  getPurchaseOption () {
    const radios = queryShadowAll(this.scopeRoot(), RC_PURCHASE_OPTION)
    const checked = radios.find((r) => r.checked)
    return checked ? checked.value : 'subscription'
  }

  emit () {
    const variantData = this.planPrices[this.variantId] || {}
    const planId = this.select?.value
    const isSubscription = this.getPurchaseOption() !== 'onetime'
    const cadence = isSubscription ? 'subscription' : 'one-time'
    const planPrice = isSubscription && planId ? (variantData.plans?.[planId] ?? null) : null

    // TEMP DEBUG: confirms our DOM `change` listeners fire on period / S&S-vs-OTP toggle.
    if (RC_DEBUG) {
      console.log('[rc-probe] selection changed →', {
        cadence,
        purchaseOption: this.getPurchaseOption(),
        planId: planId || null,
        planLabel: this.select?.selectedOptions?.[0]?.textContent?.trim() || null,
        variantId: this.variantId,
        planPrice
      })
    }

    publish(EVENTS.cadenceChange, {
      detail: {
        productId: this.productId,
        sectionId: this.sectionId,
        cadence,
        plan: isSubscription && planId ? { id: planId } : null,
        variant_price: variantData.price,
        plan_price: planPrice,
        variant_compare_price: variantData.compare
      }
    })
  }

  onVariantChange (e) {
    const { productId, sectionId, variant } = e.detail
    if (String(productId) !== String(this.productId) || String(sectionId) !== String(this.sectionId)) return
    if (variant?.id) this.variantId = String(variant.id)
    // Recharge re-renders its widget (and may replace the <select>) for the new
    // variant. Re-find/re-bind and re-emit after it settles — twice, to catch a
    // late re-render.
    this.rebind()
    setTimeout(() => this.rebind(), 350)
  }

  rebind () {
    const select = this.findSelect()
    if (select && select !== this.select) {
      this.unbind()
      this.bind(select)
    } else {
      this.bindPurchaseOptions()
    }
    this.emit()
  }
}

function init () {
  document
    .querySelectorAll('ia-main-product[data-use-recharge="true"]')
    .forEach((host) => new RechargeAdapter(host))
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
